// Pure calculation functions for the Lease Termination form, ported from the legacy
// WinForms app. Keep these free of React/state so they stay independently testable.

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const round = (value, decimals) => {
    const factor = Math.pow(10, decimals);
    return Math.round((value + Number.EPSILON) * factor) / factor;
};

// Plain inclusive day count (no leap-year adjustment) — the day-count method used
// for every company except Kanoo. Uses midnight-to-midnight on both ends: dividing a
// 00:00-to-23:59:59 span by MS_PER_DAY lands just under a whole number of days and
// rounds *up*, silently over-counting by one — use the date boundaries only.
export const inclusiveDayCount = (fromDate, toDate) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const fromStart = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const toStart = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.round((toStart.getTime() - fromStart.getTime()) / MS_PER_DAY) + 1;
};

const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

// Mirrors .NET's DateTime.AddYears: Feb 29 clamps to Feb 28 when the target year isn't leap.
const addYears = (date, years) => {
    const day = date.getDate();
    const result = new Date(date.getFullYear() + years, date.getMonth(), 1);
    const daysInTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, daysInTargetMonth));
    return result;
};

const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

// Exact port of the legacy WinForms frmLT.GetDateDiff(FromDate, ToDate) — the Kanoo-only
// day-count. Inclusive day count between fromDate/toDate, minus 1 for each year-long
// window (walked from fromDate) that straddles Feb 29 of a leap year on one side only.
// Do not "simplify" this — it must match the legacy output exactly, bugs and all.
export const getDateDiff = (fromDate, toDate) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);

    const fromStart = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const toStart = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    const inclusiveDays = Math.round((toStart.getTime() - fromStart.getTime()) / MS_PER_DAY) + 1;

    let adjustment = 0;
    let windowStart = from; // dateTime4
    let windowEnd = (from.getDate() === 29 && from.getMonth() === 1 && isLeapYear(from.getFullYear()))
        ? addYears(from, 1)
        : addDays(addYears(from, 1), -1); // t1 — no Feb-29 recheck on later iterations, matching the source

    let yearSpan = to.getFullYear() - from.getFullYear();
    if (yearSpan > 0) yearSpan -= 1;

    for (let i = 0; i <= yearSpan; i++) {
        if (windowEnd > to) windowEnd = to;

        const startLeap = isLeapYear(windowStart.getFullYear());
        const startMonth = windowStart.getMonth() + 1; // 1-indexed to match the source
        const endLeap = isLeapYear(windowEnd.getFullYear());
        const endMonth = windowEnd.getMonth() + 1;

        if (
            (startLeap && startMonth < 2 && endLeap && endMonth >= 2) ||
            (startLeap && startMonth < 3 && !endLeap && endMonth <= 2) ||
            (!startLeap && startMonth >= 3 && endLeap && endMonth >= 2)
        ) {
            adjustment += 1;
        }

        windowStart = addDays(windowEnd, 1);
        windowEnd = addDays(addYears(windowStart, 1), -1);
    }

    return inclusiveDays - adjustment;
};

// 30/360 day-count convention (every month = 30 days, every year = 360) — same
// convention as calculate30360Days in addLease.js/unitDetails.js/editLease.js (used
// there for the rent-free-day proration). For Kanoo, termination uses this for both
// the total lease term denominator (leaseStart -> leaseEnd) and days stayed
// (leaseStart -> vacatingDate) when prorating rent.
export const calculateLeaseTermDays30360 = (fromDate, toDate) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    // leaseEnd is stored as the inclusive last day of the lease (leaseStart + 1 year -
    // 1 day), so shift it to the exclusive boundary first, or a full 12-month term
    // comes out as 359 instead of 360.
    to.setDate(to.getDate() + 1);

    const yearFrom = from.getFullYear();
    const yearTo = to.getFullYear();
    const monthFrom = from.getMonth() + 1;
    const monthTo = to.getMonth() + 1;
    const dayFrom = from.getDate();
    const dayTo = to.getDate();

    let adjustment = 0;
    if (dayFrom === 31) {
        adjustment = 1;
    } else if (dayTo === 31 && (dayFrom === 30 || dayFrom === 31)) {
        adjustment = 1;
    }

    return (yearTo - yearFrom) * 360 + (monthTo - monthFrom) * 30 + (dayTo - dayFrom) - adjustment;
};

// Recomputes vat (and, for tax-inclusive rows, rate net of vat) for one deduction row.
// Returns { rate, vat } — rate is only changed for taxInclusive rows.
export const updateTax = ({ taxCode, rate, quantity, taxInclusive, taxMaster, transactionDate }) => {
    const numericRate = parseFloat(rate) || 0;
    const numericQty = parseFloat(quantity) || 0;
    const tax = (taxMaster || []).find((t) => String(t.taxCode) === String(taxCode));

    if (!tax || (tax.effectiveFromDate && new Date(tax.effectiveFromDate) > new Date(transactionDate))) {
        return { rate: numericRate, vat: 0 };
    }

    if (taxInclusive) {
        const netRate = round(numericRate / (1 + tax.rate / 100), 2);
        const vatPerUnit = round(netRate * (tax.rate / 100), 2);
        return { rate: numericRate - vatPerUnit, vat: vatPerUnit * numericQty };
    }

    return { rate: numericRate, vat: round((numericRate * numericQty * tax.rate) / 100, 2) };
};

// Rolls deduction rows + rent balance + deposit up into the summary totals shown on the form.
export const recalcSummary = ({ deductions, rentAmountBalance, deposit }) => {
    const rows = deductions || [];

    const totalDeductionInRent = rows
        .filter((row) => row.isSelected && row.isRent)
        .reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);

    const totalDeductionInSD = rows
        .filter((row) => row.isSelected && !row.isRent)
        .reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);

    const rentBalanceCalc = (parseFloat(rentAmountBalance) || 0) + totalDeductionInRent;
    const sdBalanceCalc = (parseFloat(deposit) || 0) - totalDeductionInSD;
    const finalAmount = rentBalanceCalc - sdBalanceCalc;

    return {
        totalDeductionInRent,
        totalDeductionInSD,
        totalRentBalance: rentBalanceCalc > 0 ? rentBalanceCalc : -rentBalanceCalc,
        rentBalanceLabel: rentBalanceCalc > 0 ? 'Rent Balance' : 'Rent Refund',
        sdBalance: sdBalanceCalc < 0 ? -sdBalanceCalc : sdBalanceCalc,
        sdBalanceLabel: sdBalanceCalc < 0 ? 'SD Balance' : 'SD Refund',
        finalAmount,
        finalLabel: finalAmount >= 0 ? 'Total Receivable' : 'Total Payable',
        finalColor: finalAmount >= 0 ? 'teal' : 'crimson',
    };
};
